import { useState } from "react";
import { Button, IconButton, Textarea } from "@altimateai/lego";
import { DeleteIcon, EditIcon } from "@assets/icons";
import type { OpportunityComment } from "../types";
import { OwnerAvatar } from "../components/ownerPicker";
import { formatRelative } from "./utils";

interface CommentRowProps {
  comment: OpportunityComment;
  currentUserId: number | undefined;
  authorName: string;
  onEdit: (id: string, text: string) => Promise<boolean>;
  onDelete: (id: string) => Promise<void>;
  isPending: boolean;
}

// Comment row matched to the "Discover (A)" design: a coloured author avatar
// next to the author name, relative time and comment text. The author's
// edit/delete controls (existing behaviour) are revealed on row hover so the
// resting state matches the design exactly.
export const CommentRow = ({
  comment,
  currentUserId,
  authorName,
  onEdit,
  onDelete,
  isPending,
}: CommentRowProps) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.comment);
  const isAuthor = currentUserId != null && comment.created_by === currentUserId;

  return (
    <div className="al-group" style={{ display: "flex", gap: 12 }}>
      <OwnerAvatar label={authorName} value={String(comment.created_by ?? "?")} size={32} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            marginBottom: 3,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: "#101828" }}>{authorName}</span>
            <span style={{ fontSize: 12, color: "#98A2B3", whiteSpace: "nowrap" }}>
              {formatRelative(comment.timestamp)}
              {comment.edited ? " · edited" : ""}
            </span>
          </div>
          {isAuthor && !editing && (
            <div
              className="al-opacity-0 group-hover:al-opacity-100 group-focus-within:al-opacity-100 al-transition-opacity"
              style={{ display: "flex", gap: 2, flexShrink: 0 }}
            >
              <IconButton
                title="Edit comment"
                variant="ghost"
                onClick={() => {
                  setDraft(comment.comment);
                  setEditing(true);
                }}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                title="Delete comment"
                variant="ghost"
                onClick={() => onDelete(comment.id)}
                disabled={isPending}
              >
                <DeleteIcon />
              </IconButton>
            </div>
          )}
        </div>

        {editing ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
            <Textarea value={draft} onChange={e => setDraft(e.target.value)} rows={3} />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditing(false);
                  setDraft(comment.comment);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={isPending || !draft.trim()}
                onClick={async () => {
                  const ok = await onEdit(comment.id, draft.trim());
                  if (ok) setEditing(false);
                }}
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <p style={{ margin: 0, fontSize: 13.5, color: "#475467", lineHeight: 1.55 }}>
            {comment.comment}
          </p>
        )}
      </div>
    </div>
  );
};
